import { lazy, Suspense, useEffect } from 'react';
import { Navigate, RouteObject, useNavigate } from 'react-router-dom';
import InvitationDetector from './components/auth/InvitationDetector';
import WorkflowApprovalRedirect, { APPROVAL_REDIRECT_KEY } from './components/workflows/WorkflowApprovalRedirect';
import InviteUserPage from './pages/UserManagement/InviteUser';
import UserListPage from './pages/UserManagement/UserList';

// RFID Pages
import ReaderListPage from './pages/RFID/ReaderListPage';
import ReaderDetailPage from './pages/RFID/ReaderDetailPage';
import ReaderCreatePage from './pages/RFID/ReaderCreatePage';
import ReaderEditPage from './pages/RFID/ReaderEditPage';
import ReaderTypeListPage from './pages/RFID/ReaderTypeListPage';
import ReaderTypeDetailPage from './pages/RFID/ReaderTypeDetailPage';
import ReaderTypeCreatePage from './pages/RFID/ReaderTypeCreatePage';
import ReaderTypeEditPage from './pages/RFID/ReaderTypeEditPage';
import KeyGenerationPage from './pages/RFID/KeyGenerationPage';
import KeyRevocationPage from './pages/RFID/KeyRevocationPage';
import AssetScanListPage from './pages/RFID/AssetScanListPage';
import ReaderActivityLogsPage from './pages/RFID/ReaderActivityLogsPage';
import VehicleListPage from './pages/RFID/VehicleListPage';
import VehicleCreatePage from './pages/RFID/VehicleCreatePage';
import VehicleDetailPage from './pages/RFID/VehicleDetailPage';
import VehicleEditPage from './pages/RFID/VehicleEditPage';
import MovementHistoryDashboardPage from './pages/RFID/MovementHistoryDashboardPage';
import TransferMatchingConfigListPage from './pages/RFID/TransferMatchingConfigListPage';
import TransferMatchingConfigCreatePage from './pages/RFID/TransferMatchingConfigCreatePage';
import TransferMatchingConfigEditPage from './pages/RFID/TransferMatchingConfigEditPage';
import NotificationListPage from './pages/RFID/NotificationListPage';

import UserDetail from './pages/UserManagement/UserDetail';
import UserEdit from './pages/UserManagement/UserEdit';
import ProfilePage from './pages/userProfile/ProfilePage';
import ScheduleListPage from './pages/ScheduleManagement/ScheduleListPage';
import ScheduleCreatePage from './pages/ScheduleManagement/ScheduleCreatePage';
import ScheduleDetailPage from './pages/ScheduleManagement/ScheduleDetailPage';
import ScheduleEditPage from './pages/ScheduleManagement/ScheduleEditPage';

// Auth Pages
const InvitationPage = lazy(() => import('./pages/Auth/InvitationPage'));
const AuthentikCallback = lazy(() => import('./components/AuthentikCallback'));
const Logout = lazy(() => import('./pages/Logout'));

// One-Off Activity Page
const OneOffActivityCreatePage = lazy(() => import('./pages/ScheduleManagement/OneOffActivityCreatePage'));

// Layouts
const MainLayout = lazy(() => import('./components/layout/MainLayout'));

// Guards
const RequireAuth = lazy(() => import('./components/guards/RequireAuth'));
import RequirePermission from './components/guards/RequirePermission';

// Common components
const LoadingScreen = lazy(() => import('./components/common/LoadingScreen'));
const RequireRole = lazy(() => import('./components/guards/RequireRole'));

// Debug components
//const Auth0Debug = lazy(() => import('./components/debug/Auth0Debug'));

// Pages
const Dashboard = lazy(() => import('./pages/Dashboard'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const NotificationPreferencesPage = lazy(
  () => import('./pages/NotificationPreferencesPage')
);

// Bulk Upload Pages
const BulkUpload = lazy(() => import('./components/bulk-upload/BulkUpload'));
const BulkUploadFilePage = lazy(() => import('./components/bulk-upload/BulkUploadFilePage'));

// Reports Pages
const ReportsList = lazy(() => import('./pages/Reports/ReportsList'));
const AssetTransferReports = lazy(() => import('./pages/Reports/AssetTransferReports/AssetTransferReports'));
const AssetDisposalReports = lazy(() => import('./pages/Reports/AssetDisposalReports/AssetDisposalReports'));
const AssetLiveReports = lazy(() => import('./pages/Reports/AssetLiveReports'));
const AssetTaggingReports = lazy(() => import('./pages/Reports/AssetTaggingReport'));
const AssetLocationMismatchReport = lazy(() => import('./pages/Reports/AssetLocationMismatchReport'));
const ReaderWiseAssetReport = lazy(() => import('./pages/Reports/ReaderWiseAssetReport'));
const UnregisteredAssetMovementReport = lazy(() => import('./pages/Reports/UnregisteredAssetMovementReport'));
const AssetUtilizationReport = lazy(() => import('./pages/Reports/AssetUtilizationReport'));
const AssetValuationReports = lazy(() => import('./pages/Reports/AssetValuationReports'));
const AuditReportsWithTabs = lazy(() => import('./pages/Reports/AuditReportsWithTabs'));
const AuditAssetReports = lazy(() => import('./pages/Reports/AuditAssetReports'));
const AuditRequestReports = lazy(() => import('./pages/Reports/AuditRequestReports'));
const ActivityCostReport = lazy(() => import('./pages/Reports/ActivityCostReport'));
const ActivitySLAReport = lazy(() => import('./pages/Reports/ActivitySLAReport'));
const AssetList = lazy(() => import('./pages/AssetManagement/AssetList'));
const AssetDetails = lazy(() => import('./pages/AssetManagement/AssetDetails'));
const AssetCreate = lazy(() => import('./pages/AssetManagement/AssetCreate'));
const AssetUpdate = lazy(() => import('./pages/AssetManagement/UpdateAsset'));
const AssetMasterCreate = lazy(
  () => import('./pages/AssetManagement/AssetMasterCreate')
);
const NotFound = lazy(() => import('./pages/NotFound'));
const Unauthorized = lazy(() => import('./pages/Unauthorized'));

// Asset Transfer Pages
const AssetTransferPage = lazy(() => import('./pages/AssetTransfer/AssetTransferPage'));
const AssetTransferFormPage = lazy(() => import('./pages/AssetTransfer/AssetTransferFormPage'));
const BulkAssetTransferPage = lazy(() => import('./pages/AssetTransfer/BulkAssetTransferPage'));
const AssetTransferDetailPage = lazy(() => import('./pages/AssetTransfer/AssetTransferDetailPage'));

// Asset Disposal Pages
const AssetDisposalListPage = lazy(() => import('./pages/AssetDisposal/AssetDisposalListPage'));
const AssetDisposalPage = lazy(() => import('./pages/AssetDisposal/AssetDisposalPage'));
const AssetDisposalDetailPage = lazy(() => import('./pages/AssetDisposal/AssetDisposalDetailPage'));

//Asset Tag Template Pages
const AssetTagTemplateListPage = lazy(() => import('./pages/AssetTagTemplate/AssetTagTemplateList'));
const AssetTagTemplateDesignerPage = lazy(() => import('./pages/AssetTagTemplate/AssetTagTemplateDesigner'));
const AssetTagTemplateDetailPage = lazy(() => import('./pages/AssetTagTemplate/AssetTagTemplateDetail'));

// Audit Management Pages
const AuditRequestListPage = lazy(() => import('./pages/AuditManagement/AuditRequestListPage'));
const AuditRequestFormPageUnified = lazy(() => import('./pages/AuditManagement/AuditRequestFormPageUnified'));
const AuditRequestDetailPage = lazy(() => import('./pages/AuditManagement/AuditRequestDetailPage'));
const PendingAuditsPage = lazy(() => import('./pages/AuditManagement/PendingAuditsPage'));
const AuditAssetsPage = lazy(() => import('./pages/AuditManagement/AuditAssetsPage'));
const AuditReverificationPage = lazy(() => import('./pages/AuditManagement/AuditReverificationPage'));
const ReverifierAuditsPage = lazy(() => import('./pages/AuditManagement/ReverifierAuditsPage'));
const RFIDScannerPage = lazy(() => import('./pages/AuditManagement/RFIDScannerPage'));

// RFID Management Pages - Deprecated duplicate implementation
// @deprecated - These imports are for a duplicate implementation that was accidentally pulled
// JIRA Ticket: [TICKET-NUMBER] - RFID Reader Duplication Fix
// const RFIDManagementPage = lazy(
//   () => import('./pages/RFIDManagement')
// );
// const ReaderForm = lazy(
//   () => import('./pages/RFIDManagement/components/ReaderForm')
// );
// const ReaderTypeForm = lazy(
//   () => import('./pages/RFIDManagement/components/ReaderTypeForm')
// );
// const LocationAssignmentForm = lazy(
//   () => import('./pages/RFIDManagement/components/LocationAssignmentForm')
// );

// Manufacturer Management Pages
const ManufacturerList = lazy(() => import('./pages/ManufacturerManagement/ManufacturerList'));
const ManufacturerDetail = lazy(() => import('./pages/ManufacturerManagement/ManufacturerDetail'));
const ManufacturerCreate = lazy(() => import('./pages/ManufacturerManagement/ManufacturerCreate'));
const ManufacturerEdit = lazy(() => import('./pages/ManufacturerManagement/ManufacturerEdit'));
const ManufacturerHistory = lazy(() => import('./pages/ManufacturerManagement/ManufacturerHistory'));

// Model Management Pages
const ModelList = lazy(() => import('./pages/ModelManagement/ModelList'));
const ModelDetail = lazy(() => import('./pages/ModelManagement/ModelDetail'));
const ModelCreate = lazy(() => import('./pages/ModelManagement/ModelCreate'));
const ModelEdit = lazy(() => import('./pages/ModelManagement/ModelEdit'));
const ModelHistory = lazy(() => import('./pages/ModelManagement/ModelHistory'));

// Department Management Pages
const DepartmentList = lazy(() => import('./pages/DepartmentManagement/DepartmentList'));
const DepartmentDetail = lazy(() => import('./pages/DepartmentManagement/DepartmentDetail'));
const DepartmentCreate = lazy(() => import('./pages/DepartmentManagement/DepartmentCreate'));
const DepartmentEdit = lazy(() => import('./pages/DepartmentManagement/DepartmentEdit'));
const DepartmentHistory = lazy(() => import('./pages/DepartmentManagement/DepartmentHistory'));

// Asset Category Management Pages
const AssetCategoryList = lazy(() => import('./pages/AssetCategoryManagement/AssetCategoryList'));
const AssetCategoryDetail = lazy(() => import('./pages/AssetCategoryManagement/AssetCategoryDetail'));
const AssetCategoryCreate = lazy(() => import('./pages/AssetCategoryManagement/AssetCategoryCreate'));
const AssetCategoryEdit = lazy(() => import('./pages/AssetCategoryManagement/AssetCategoryEdit'));
const AssetCategoryHierarchy = lazy(() => import('./pages/AssetCategoryManagement/AssetCategoryHierarchy'));
const AssetCategoryHistory = lazy(() => import('./pages/AssetCategoryManagement/AssetCategoryHistory'));

// Asset Status Management Pages
const AssetStatusesListPage = lazy(() => import('./pages/AssetStatuses/AssetStatusesListPage'));
const AssetStatusCreatePage = lazy(() => import('./pages/AssetStatuses/AssetStatusCreatePage'));
const AssetStatusEditPage = lazy(() => import('./pages/AssetStatuses/AssetStatusEditPage'));
const AssetStatusDetailPage = lazy(() => import('./pages/AssetStatuses/AssetStatusDetailPage'));
const AssetStatusHistory = lazy(() => import('./pages/AssetStatuses/AssetStatusHistory'));

// Asset Conditions Management Pages
const AssetConditionsListPage = lazy(() => import('./pages/AssetConditions/AssetConditionsListPage'));
const AssetConditionCreatePage = lazy(() => import('./pages/AssetConditions/AssetConditionCreatePage'));
const AssetConditionEditPage = lazy(() => import('./pages/AssetConditions/AssetConditionEditPage'));
const AssetConditionDetailPage = lazy(() => import('./pages/AssetConditions/AssetConditionDetailPage'));
const AssetConditionHistory = lazy(() => import('./pages/AssetConditions/AssetConditionHistory'));

// Site Management Pages
const SiteList = lazy(() => import('./pages/SiteManagement/SiteList'));
const SiteDetail = lazy(() => import('./pages/SiteManagement/SiteDetail'));
const SiteCreate = lazy(() => import('./pages/SiteManagement/SiteCreate'));
const SiteEdit = lazy(() => import('./pages/SiteManagement/SiteEdit'));
const SiteHistory = lazy(() => import('./pages/SiteManagement/SiteHistory'));

// Location Management Pages
const LocationList = lazy(() => import('./pages/LocationManagement/LocationList'));
const LocationDetail = lazy(() => import('./pages/LocationManagement/LocationDetail'));
const LocationCreate = lazy(() => import('./pages/LocationManagement/LocationCreate'));
const LocationEdit = lazy(() => import('./pages/LocationManagement/LocationEdit'));
const LocationHistory = lazy(() => import('./pages/LocationManagement/LocationHistory'));

// Floor Plan Management Pages
const FloorPlanListPage = lazy(() => import('./pages/FloorPlanManagement/FloorPlanListPage'));
const InteractiveFloorPlanPage = lazy(() => import('./pages/FloorPlanManagement/InteractiveFloorPlanPage'));
const InteractiveEditorPage = lazy(() => import('./pages/FloorPlanManagement/InteractiveEditorPage'));
// const FloorPlanUploadPage = lazy(() => import('./pages/FloorPlanManagement/FloorPlanUploadPage'));
const FloorPlanViewPage = lazy(() => import('./pages/FloorPlanManagement/FloorPlanViewPage'));
const FloorPlanEditPage = lazy(() => import('./pages/FloorPlanManagement/FloorPlanEditPage'));

// Barcode Examples Page
const BarcodeExamples = lazy(() => import('./pages/BarcodeExamples'));

// Supplier Management Pages
const SupplierList = lazy(() => import('./pages/SupplierManagement/SupplierList'));
const SupplierDetail = lazy(() => import('./pages/SupplierManagement/SupplierDetail'));
const SupplierCreate = lazy(() => import('./pages/SupplierManagement/SupplierCreate'));
const SupplierEdit = lazy(() => import('./pages/SupplierManagement/SupplierEdit'));
const SupplierHistory = lazy(() => import('./pages/SupplierManagement/SupplierHistory'));

// Workflow Management Pages
const WorkflowTemplateList = lazy(() => import('./pages/WorkflowManagement/WorkflowTemplateList'));
const WorkflowTemplateDetail = lazy(() => import('./pages/WorkflowManagement/WorkflowTemplateDetail'));
const WorkflowTemplateCreate = lazy(() => import('./pages/WorkflowManagement/WorkflowTemplateCreate'));
const WorkflowTemplateEdit = lazy(() => import('./pages/WorkflowManagement/WorkflowTemplateEdit'));
const WorkflowPriorityManager = lazy(() => import('./pages/WorkflowManagement/WorkflowPriorityManager'));
const WorkflowConditionBuilder = lazy(() => import('./pages/WorkflowManagement/WorkflowConditionBuilder'));
const WorkflowApprovalConfig = lazy(() => import('./pages/WorkflowManagement/WorkflowApprovalConfig'));
const WorkflowRoleManagement = lazy(() => import('./pages/WorkflowManagement/WorkflowRoleManagement'));
const ApprovalTaskDashboard = lazy(() => import('./pages/WorkflowManagement/ApprovalTaskDashboard'));
const WorkflowTransactionDashboard = lazy(() => import('./pages/WorkflowManagement/WorkflowTransactionDashboard'));
const WorkflowAnalyticsDashboard = lazy(() => import('./pages/WorkflowManagement/WorkflowAnalyticsDashboard'));
const WorkflowAuditTrail = lazy(() => import('./pages/WorkflowManagement/WorkflowAuditTrail'));

// Depreciation Management Pages
const DepreciationDashboard = lazy(() => import('./pages/DepreciationManagement/DepreciationDashboard'));
const AssetDepreciationDetail = lazy(() => import('./pages/DepreciationManagement/AssetDepreciationDetail'));

// Tenant Management Pages
const TenantList = lazy(() => import('./components/tenants/TenantList/TenantList'));
const TenantCreate = lazy(() => import('./components/tenants/TenantCreate/TenantCreate'));

// Role Management Pages
const RoleManagement = lazy(() => import('./pages/RoleManagement/RoleManagement'));
const CreateRole = lazy(() => import('./pages/RoleManagement/CreateRole'));
const EditRole = lazy(() => import('./pages/RoleManagement/EditRole'));
const RoleDetails = lazy(() => import('./pages/RoleManagement/RoleDetails'));

// Activity Type Management Pages
const ActivityTypesPage = lazy(() => import('./pages/ActivityTypes/ActivityTypesPage'));
const ActivityTypeDetailPage = lazy(() => import('./pages/ActivityTypes/ActivityTypeDetailPage'));
const ActivityTypeCreatePage = lazy(() => import('./pages/ActivityTypes/ActivityTypeCreatePage'));
const ActivityTypeEditPage = lazy(() => import('./pages/ActivityTypes/ActivityTypeEditPage'));

// User Group Management Pages
const UserGroupsPage = lazy(() => import('./pages/UserGroups/UserGroupsPage'));
const UserGroupDetailPage = lazy(() => import('./pages/UserGroups/UserGroupDetailPage'));
const UserGroupCreatePage = lazy(() => import('./pages/UserGroups/UserGroupCreatePage'));
const UserGroupEditPage = lazy(() => import('./pages/UserGroups/UserGroupEditPage'));

// Cost Template Management Pages
const CostTemplatesPage = lazy(() => import('./pages/CostTemplates/CostTemplatesPage'));
const CostTemplateDetailPage = lazy(() => import('./pages/CostTemplates/CostTemplateDetailPage'));
const CostTemplateCreatePage = lazy(() => import('./pages/CostTemplates/CostTemplateCreatePage'));
const CostTemplateEditPage = lazy(() => import('./pages/CostTemplates/CostTemplateEditPage'));

// Checklist Template Management Pages
const ChecklistTemplatesPage = lazy(() => import('./pages/ChecklistTemplates/ChecklistTemplatesPage'));
const ChecklistTemplateDetailPage = lazy(() => import('./pages/ChecklistTemplates/ChecklistTemplateDetailPage'));
const ChecklistTemplateCreatePage = lazy(() => import('./pages/ChecklistTemplates/ChecklistTemplateCreatePage'));
const ChecklistTemplateEditPage = lazy(() => import('./pages/ChecklistTemplates/ChecklistTemplateEditPage'));

// Import actual Schedule Management Pages
import ActivityListPage from './pages/ScheduleManagement/ActivityListPage';
import ActivityDetailPage from './pages/ScheduleManagement/ActivityDetailPage';
import ActivityExecutePage from './pages/ScheduleManagement/ActivityExecutePage';
// Custom Forms Management Pages
const CustomFormsAdmin = lazy(() => import('./pages/CustomForms/CustomFormsAdmin'));
const CustomFieldsManager = lazy(() => import('./components/customForms/CustomFieldsManager/CustomFieldsManager'));
const ListMastersPage = lazy(() => import('./components/customForms/ListMasters/ListMasters'));
const ListMasterOptionsPage = lazy(() => import('./components/customForms/ListMasterOptions/ListMasterOptions'));

// Placeholder components for new menu items
//const OrgLevelUserAdmin = () => <div>Org-Level User Admin</div>;

const AUTH_PROVIDER = import.meta.env.VITE_AUTH_PROVIDER || 'auth0';

const authRoutes: RouteObject[] = AUTH_PROVIDER === 'authentik'
  ? [{ path: 'callback', element: <Suspense fallback={<LoadingScreen />}><AuthentikCallback /></Suspense> }]
  : [{ path: 'invite', element: <Suspense fallback={<LoadingScreen />}><InvitationPage /></Suspense> }];

// Define routes
const routes: RouteObject[] = [
  // Root path handler - checks for invitation parameters before auth
  {
    path: '/',
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <InvitationDetector />
        <RequireAuth>
          <MainLayout />
        </RequireAuth>
      </Suspense>
    ),
    children: [
      {
        path: '/',
        element: <Navigate to="/dashboard" />,
      },
      {
        path: 'dashboard',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <Dashboard />
          </Suspense>
        ),
      },
      {
        path: 'profile',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <RequirePermission requiredPermission="user_profile_menu">
              <ProfilePage />
            </RequirePermission>
          </Suspense>
        ),
      },
      {
        path: 'assets',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_asset_scr">
                  <AssetList />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_asset_scr">
                  <AssetCreate />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_asset_scr">
                  <AssetDetails />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="edit_asset_scr">
                  <AssetUpdate />
                </RequirePermission>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'assets/master',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <AssetList />
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <AssetMasterCreate />
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <AssetDetails />
              </Suspense>
            ),
          },
        ],
      },
      // Asset Transfer routes
      {
        path: 'asset-transfer',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_asset_transfer_scr">
                  <AssetTransferPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'new',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_asset_transfer_scr">
                  <AssetTransferFormPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'bulk',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_asset_transfer_scr">
                  <BulkAssetTransferPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_asset_transfer_scr">
                  <AssetTransferDetailPage />
                </RequirePermission>
              </Suspense>
            ),
          },
        ],
      },
      // Asset Disposal routes
      {
        path: 'asset-disposal',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_asset_disposal_scr">
                  <AssetDisposalListPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'new',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_asset_disposal_scr">
                  <AssetDisposalPage mode="single" />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'bulk',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_asset_disposal_scr">
                  <AssetDisposalPage mode="bulk" />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_asset_disposal_scr">
                  <AssetDisposalDetailPage />
                </RequirePermission>
              </Suspense>
            ),
          },
        ],
      },
      // Asset Tag Template routes
      {
        path: 'asset-tag-template',
        children: [
          {
            path: '', 
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <AssetTagTemplateListPage />
              </Suspense>
            ),
          },
          {
            path: 'new',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <AssetTagTemplateDesignerPage/>
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <AssetTagTemplateDesignerPage/>
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <AssetTagTemplateDetailPage/>
              </Suspense>
            ),
          },
        ],
      },
      // Audit Management routes
      {
        path: 'audit',
        children: [
          {
            path: 'requests',
            children: [
              {
                path: '',
                element: (
                  <Suspense fallback={<LoadingScreen />}>
                    <RequirePermission requiredPermission="view_audit_scr">
                      <AuditRequestListPage />
                    </RequirePermission>
                  </Suspense>
                ),
              },
              {
                path: 'create',
                element: (
                  <Suspense fallback={<LoadingScreen />}>
                    <RequirePermission requiredPermission="add_audit_scr">
                      <AuditRequestFormPageUnified />
                    </RequirePermission>
                  </Suspense>
                ),
              },
              {
                path: ':id',
                element: (
                  <Suspense fallback={<LoadingScreen />}>
                    <RequirePermission requiredPermission="view_audit_scr">
                      <AuditRequestDetailPage />
                    </RequirePermission>
                  </Suspense>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <Suspense fallback={<LoadingScreen />}>
                    <RequirePermission requiredPermission="edit_audit_scr">
                      <AuditRequestFormPageUnified />
                    </RequirePermission>
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'pending',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_audit_task_scr">
                  <PendingAuditsPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'reverifier',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_audit_task_scr">
                  <ReverifierAuditsPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id/assets',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <AuditAssetsPage filter="all" />
              </Suspense>
            ),
          },
          {
            path: ':id/assets/verified',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <AuditAssetsPage filter="verified" />
              </Suspense>
            ),
          },
          {
            path: ':id/assets/unverified',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <AuditAssetsPage filter="unverified" />
              </Suspense>
            ),
          },
          {
            path: ':id/reverify',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <AuditReverificationPage />
              </Suspense>
            ),
          },
          {
            path: 'rfid-scanner',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_rfid_scr">
                  <RFIDScannerPage />
                </RequirePermission>
              </Suspense>
            ),
          },
        ],
      },
      // Manufacturer routes
      {
        path: 'manufacturers',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_manufacturer_scr">
                  <ManufacturerList />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_manufacturer_scr">
                  <ManufacturerCreate />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_manufacturer_scr">
                  <ManufacturerDetail />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="edit_manufacturer_scr">
                  <ManufacturerEdit />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'history/:id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_manufacturer_scr">
                  <ManufacturerHistory />
                </RequirePermission>
              </Suspense>
            ),
          },
        ],
      },
      // Model routes
      {
        path: 'models',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_model_scr">
                  <ModelList />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_model_scr">
                  <ModelCreate />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_model_scr">
                  <ModelDetail />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="edit_model_scr">
                  <ModelEdit />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'history/:id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_model_scr">
                  <ModelHistory />
                </RequirePermission>
              </Suspense>
            ),
          },
        ],
      },
      // Department routes
      {
        path: 'departments',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_department_scr">
                  <DepartmentList />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_department_scr">
                  <DepartmentCreate />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_department_scr">
                  <DepartmentDetail />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="edit_department_scr">
                  <DepartmentEdit />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'history/:id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_department_scr">
                  <DepartmentHistory />
                </RequirePermission>
              </Suspense>
            ),
          },
        ],
      },
      // Asset Category routes
      {
        path: 'asset-categories',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_asset_category_scr">
                  <AssetCategoryList />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_asset_category_scr">
                  <AssetCategoryCreate />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'hierarchy',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <AssetCategoryHierarchy />
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_asset_category_scr">
                  <AssetCategoryDetail />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="edit_asset_category_scr">
                  <AssetCategoryEdit />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'history/:id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_asset_category_scr">
                  <AssetCategoryHistory />
                </RequirePermission>
              </Suspense>
            ),
          },
        ],
      },
      // Asset Status routes
      {
        path: 'asset-statuses',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_asset_status_scr">
                  <AssetStatusesListPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_asset_status_scr">
                  <AssetStatusCreatePage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_asset_status_scr">
                  <AssetStatusDetailPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="edit_asset_status_scr">
                  <AssetStatusEditPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'history/:id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_asset_status_scr">
                  <AssetStatusHistory />
                </RequirePermission>
              </Suspense>
            ),
          },
        ],
      },
      // Asset Conditions routes
      {
        path: 'asset-conditions',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_asset_condition_scr">
                  <AssetConditionsListPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_asset_condition_scr">
                  <AssetConditionCreatePage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_asset_condition_scr">
                  <AssetConditionDetailPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="edit_asset_condition_scr">
                  <AssetConditionEditPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'history/:id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_asset_condition_scr">
                  <AssetConditionHistory />
                </RequirePermission>
              </Suspense>
            ),
          },
        ],
      },
      // Role Management routes
      {
        path: 'role-management',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_role_scr">
                  <RoleManagement />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_role_scr">
                  <CreateRole />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'edit/:roleId',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="edit_role_scr">
                  <EditRole />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'details/:roleId',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_role_scr">
                  <RoleDetails />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'details/:roleId/:tab',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_role_scr">
                  <RoleDetails />
                </RequirePermission>
              </Suspense>
            ),
          },
        ],
      },
      // Site routes
      {
        path: 'sites',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_site_scr">
                  <SiteList />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_site_scr">
                  <SiteCreate />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_site_scr">
                  <SiteDetail />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="edit_site_scr">
                  <SiteEdit />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'history/:id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_site_scr">
                  <SiteHistory />
                </RequirePermission>
              </Suspense>
            ),
          },
          // History route removed as part of US-MDM-28
        ],
      },
      // Location routes
      {
        path: 'locations',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_location_scr">
                  <LocationList />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_location_scr">
                  <LocationCreate />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_location_scr">
                  <LocationDetail />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="edit_location_scr">
                  <LocationEdit />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'history/:id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_location_scr">
                  <LocationHistory />
                </RequirePermission>
              </Suspense>
            ),
          },
          // History route removed as part of US-MDM-28
        ],
      },
      // Floor Plan routes
      {
        path: 'floor-plans',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="manage_floor_plan_scr">
                  <FloorPlanListPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'interactive',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="manage_floor_plan_scr">
                  <InteractiveFloorPlanPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="manage_floor_plan_scr">
                  <FloorPlanViewPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="manage_floor_plan_scr">
                  <FloorPlanEditPage />
                </RequirePermission>
              </Suspense>
            ),
          },
        ],
      },
      // Interactive Editor routes
      {
        path: 'interactive-editor',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="manage_floor_plan_scr">
                  <InteractiveEditorPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_floor_plan_scr">
                  <InteractiveEditorPage />
                </RequirePermission>
              </Suspense>
            ),
          },
        ],
      },
      // Barcode Examples routes
      {
        path: 'barcode-examples',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <BarcodeExamples />
          </Suspense>
        ),
      },
      // Supplier routes
      {
        path: 'suppliers',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_supplier_scr">
                  <SupplierList />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_supplier_scr">
                  <SupplierCreate />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_supplier_scr">
                  <SupplierDetail />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="edit_supplier_scr">
                  <SupplierEdit />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'history/:id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_supplier_scr">
                  <SupplierHistory />
                </RequirePermission>
              </Suspense>
            ),
          },
        ],
      },
      // Workflow Template routes
      {
        path: 'workflow-templates',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_workflow_template_scr">
                  <WorkflowTemplateList />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_workflow_template_scr">
                  <WorkflowTemplateCreate />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_workflow_template_scr">
                  <WorkflowTemplateDetail />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_workflow_template_scr">
                  <WorkflowTemplateEdit />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'priorities',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <WorkflowPriorityManager />
              </Suspense>
            ),
          },
          {
            path: ':id/conditions',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <WorkflowConditionBuilder />
              </Suspense>
            ),
          },
          {
            path: ':id/approvals',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <WorkflowApprovalConfig />
              </Suspense>
            ),
          },
          {
            path: ':id/roles',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <WorkflowRoleManagement />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'approval-tasks',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_workflow_scr">
                  <ApprovalTaskDashboard />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':taskId',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_workflow_scr">
                  <ApprovalTaskDashboard />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'workflow-transactions',
            children: [
              {
                path: '',
                element: (
                  <Suspense fallback={<LoadingScreen />}>
                    <RequirePermission requiredPermission="view_workflow_scr">
                      <WorkflowTransactionDashboard />
                    </RequirePermission>
                  </Suspense>
                ),
              },
              {
                path: ':id',
                element: (
                  <Suspense fallback={<LoadingScreen />}>
                    <RequirePermission requiredPermission="view_workflow_scr">
                      <WorkflowTransactionDashboard />
                    </RequirePermission>
                  </Suspense>
                ),
              },
              {
                path: 'workflow-analytics',
                element: (
                  <Suspense fallback={<LoadingScreen />}>
                    <WorkflowAnalyticsDashboard />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
      // Notification routes
      {
        path: 'notifications',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <NotificationsPage />
          </Suspense>
        ),
      },
      {
        path: 'notification-preferences',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <NotificationPreferencesPage />
          </Suspense>
        ),
      },
      {
        path: 'workflow-audit-trail',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <WorkflowAuditTrail />
          </Suspense>
        ),
      },
      // Activity Type routes
      {
        path: 'activity-types',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_activity_type_scr">
                  <ActivityTypesPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_activity_type_scr">
                  <ActivityTypeCreatePage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_activity_type_scr">
                  <ActivityTypeDetailPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="edit_activity_type_scr">
                  <ActivityTypeEditPage />
                </RequirePermission>
              </Suspense>
            ),
          },
        ],
      },
      // User Groups routes
      {
        path: 'user-groups',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_user_group_scr">
                  <UserGroupsPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_user_group_scr">
                  <UserGroupCreatePage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_user_group_scr">
                  <UserGroupDetailPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="edit_user_group_scr">
                  <UserGroupEditPage />
                </RequirePermission>
              </Suspense>
            ),
          },
        ],
      },
      // Cost Templates routes
      {
        path: 'cost-templates',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_cost_template_scr">
                  <CostTemplatesPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_cost_template_scr">
                  <CostTemplateCreatePage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_cost_template_scr">
                  <CostTemplateDetailPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="edit_cost_template_scr">
                  <CostTemplateEditPage />
                </RequirePermission>
              </Suspense>
            ),
          },
        ],
      },
      // Checklist Templates routes
      {
        path: 'checklist-templates',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_checklist_template_scr">
                  <ChecklistTemplatesPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_checklist_template_scr">
                  <ChecklistTemplateCreatePage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_checklist_template_scr">
                  <ChecklistTemplateDetailPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="edit_checklist_template_scr">
                  <ChecklistTemplateEditPage />
                </RequirePermission>
              </Suspense>
            ),
          },
        ],
      },
      // Custom Forms Management routes
      {
        path: 'custom-forms',
        children: [
          {
            path: 'entity-forms',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_custom_form_scr">
                  <CustomFormsAdmin />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'sections/:sectionId/fields',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_custom_form_scr">
                  <CustomFieldsManager />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'list-masters',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_custom_form_scr">
                  <ListMastersPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'list-masters/:listMasterId/options',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_custom_form_scr">
                  <ListMasterOptionsPage />
                </RequirePermission>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'users',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_user_scr">
                  <UserListPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'invite',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_user_scr">
                  <InviteUserPage />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':userId',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_user_scr">
                  <UserDetail />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':userId/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="edit_user_scr">
                  <UserEdit />
                </RequirePermission>
              </Suspense>
            ),
          },
        ],
      },
      // Tenant Management routes - PoS access (no role restrictions during testing)
      {
        path: 'tenants',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <TenantList />
              </Suspense>
            )
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <TenantCreate />
              </Suspense>
            )
          },
        ]
      },
      // Debug routes
      {
        path: 'schedule-management',
        children: [
          {
            path: 'schedules',
            children: [
              {
                path: '',
                element: (
                  <RequirePermission requiredPermission="view_schedule_scr">
                    <ScheduleListPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'create',
                element: (
                  <RequirePermission requiredPermission="add_schedule_scr">
                    <ScheduleCreatePage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id',
                element: (
                  <RequirePermission requiredPermission="view_schedule_scr">
                    <ScheduleDetailPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RequirePermission requiredPermission="edit_schedule_scr">
                    <ScheduleEditPage />
                  </RequirePermission>
                ),
              },
            ],
          },
          {
            path: 'activities',
            children: [
              {
                path: '',
                element: (
                  <RequirePermission requiredPermission="view_activity_scr">
                    <ActivityListPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'create-one-off',
                element: (
                  <Suspense fallback={<LoadingScreen />}>
                    <RequirePermission requiredPermission="add_activity_scr">
                      <OneOffActivityCreatePage />
                    </RequirePermission>
                  </Suspense>
                ),
              },
              {
                path: ':id',
                element: (
                  <RequirePermission requiredPermission="view_activity_scr">
                    <ActivityDetailPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id/execute',
                element: (
                  <RequirePermission requiredPermission="execute_activity_scr">
                    <ActivityExecutePage />
                  </RequirePermission>
                ),
              },
            ],
          },
        ],
      },
      // Reports routes
      {
        path: 'reports',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <ReportsList />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'asset-transfers',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <AssetTransferReports />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'asset_transfer',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <AssetTransferReports />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'asset-transfers/:reportId',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <AssetTransferReports />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'asset-disposals',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <AssetDisposalReports />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'asset_disposal',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <AssetDisposalReports />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'asset-disposals/:reportId',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <AssetDisposalReports />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'asset-live',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <AssetLiveReports />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'asset_live',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <AssetLiveReports />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'asset-live/:reportId',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <AssetLiveReports />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'asset-utilization',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <AssetUtilizationReport />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'asset-valuation',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <AssetValuationReports />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'asset-tagging',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <AssetTaggingReports />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'asset-location-mismatch',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <AssetLocationMismatchReport />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'reader-wise-asset',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <ReaderWiseAssetReport />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'unregistered-asset-movement',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <UnregisteredAssetMovementReport />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'audit-reports',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <AuditReportsWithTabs />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'audit-asset',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <AuditAssetReports />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'audit-request',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <AuditRequestReports />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'activity-cost-report',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <ActivityCostReport />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'activity-cost',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <ActivityCostReport />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'activity-sla-report',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <ActivitySLAReport />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'activity-sla',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_report_scr">
                  <ActivitySLAReport />
                </RequirePermission>
              </Suspense>
            ),
          },
        ],
      },
      // Bulk Upload routes
      {
        path: 'bulk-upload',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_bulk_upload_scr">
                  <Navigate to="/bulk-upload/assets" />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':type',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="view_bulk_upload_scr">
                  <BulkUpload
                    pageName="Bulk Upload"
                    columns={[
                      { id: 'name', label: 'File Name', minWidth: 170 },
                      { id: 'entity', label: 'Entity', minWidth: 130 },
                      { id: 'uploadedDate', label: 'Uploaded Date', minWidth: 130 },
                      { id: 'uploadedTime', label: 'Uploaded Time', minWidth: 130 },
                      { id: 'uploadedBy', label: 'Uploaded By', minWidth: 130 },
                    ]}
                    menuItems={[
                      { id: 'sites', name: 'Sites', path: '/bulk-upload/sites' },
                      { id: 'locations', name: 'Locations', path: '/bulk-upload/locations' },
                      { id: 'categories', name: 'Asset Categories', path: '/bulk-upload/categories' },
                      { id: 'departments', name: 'Departments', path: '/bulk-upload/departments' },
                      { id: 'asset_status', name: 'Asset Status', path: '/bulk-upload/asset_status' },
                      { id: 'asset_condition', name: 'Asset Condition', path: '/bulk-upload/asset_condition' },
                      { id: 'manufacturer', name: 'Manufacturer', path: '/bulk-upload/manufacturer' },
                      { id: 'model', name: 'Model', path: '/bulk-upload/model' },
                      { id: 'supplier', name: 'Supplier', path: '/bulk-upload/supplier' },
                      { id: 'users', name: 'Users', path: '/bulk-upload/users' },
                      { id: 'assets', name: 'Assets', path: '/bulk-upload/assets' },
                      { id: 'asset_images', name: 'Asset Images', path: '/bulk-upload/asset_images' },
                      { id: 'reader', name: 'RFID Reader', path: '/bulk-upload/reader' },
                      { id: 'vehicle', name: 'Vehicle', path: '/bulk-upload/vehicle' },
                    ]}
                    apiEndpoint="bulk-upload"
                  />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: ':type/upload',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="add_bulk_upload_scr">
                  <BulkUploadFilePage
                    apiEndpoint="bulk-upload"
                  />
                </RequirePermission>
              </Suspense>
            ),
          },
        ],
      },
      // Depreciation Management routes
      {
        path: 'depreciation',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="depreciation_menu">
                  <DepreciationDashboard />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'dashboard',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequirePermission requiredPermission="depreciation_menu">
                  <DepreciationDashboard />
                </RequirePermission>
              </Suspense>
            ),
          },
          {
            path: 'asset/:assetId',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <AssetDepreciationDetail />
              </Suspense>
            ),
          },
        ],
      },
      // Notification routes
      {
        path: 'notifications',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <NotificationsPage />
          </Suspense>
        ),
      },
      {
        path: 'notification-preferences',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <NotificationPreferencesPage />
          </Suspense>
        ),
      },
      // RFID Reader Master routes
      {
        path: 'rfid/readers',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole
                  allowedRoles={['admin']}
                  redirectTo="/unauthorized"
                >
                  <ReaderListPage />
                </RequireRole>
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole
                  allowedRoles={['admin']}
                  redirectTo="/unauthorized"
                >
                  <ReaderCreatePage />
                </RequireRole>
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole
                  allowedRoles={['admin']}
                  redirectTo="/unauthorized"
                >
                  <ReaderDetailPage />
                </RequireRole>
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole
                  allowedRoles={['admin']}
                  redirectTo="/unauthorized"
                >
                  <ReaderEditPage />
                </RequireRole>
              </Suspense>
            ),
          },
          {
            path: ':id/generate-key',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole
                  allowedRoles={['admin']}
                  redirectTo="/unauthorized"
                >
                  <KeyGenerationPage />
                </RequireRole>
              </Suspense>
            ),
          },
          {
            path: ':id/revoke-key',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole
                  allowedRoles={['admin']}
                  redirectTo="/unauthorized"
                >
                  <KeyRevocationPage />
                </RequireRole>
              </Suspense>
            ),
          },
          {
            path: ':id/activity-logs',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole
                  allowedRoles={['admin']}
                  redirectTo="/unauthorized"
                >
                  <ReaderActivityLogsPage />
                </RequireRole>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'rfid/reader-types',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole
                  allowedRoles={['admin']}
                  redirectTo="/unauthorized"
                >
                  <ReaderTypeListPage />
                </RequireRole>
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole
                  allowedRoles={['admin']}
                  redirectTo="/unauthorized"
                >
                  <ReaderTypeCreatePage />
                </RequireRole>
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole
                  allowedRoles={['admin']}
                  redirectTo="/unauthorized"
                >
                  <ReaderTypeDetailPage />
                </RequireRole>
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole
                  allowedRoles={['admin']}
                  redirectTo="/unauthorized"
                >
                  <ReaderTypeEditPage />
                </RequireRole>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'rfid/asset-scans',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <RequireRole
              allowedRoles={['admin']}
              redirectTo="/unauthorized"
            >
              <AssetScanListPage />
            </RequireRole>
          </Suspense>
        ),
      },
      {
        path: 'rfid/vehicles',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole
                  allowedRoles={['admin']}
                  redirectTo="/unauthorized"
                >
                  <VehicleListPage />
                </RequireRole>
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole
                  allowedRoles={['admin']}
                  redirectTo="/unauthorized"
                >
                  <VehicleCreatePage />
                </RequireRole>
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole
                  allowedRoles={['admin']}
                  redirectTo="/unauthorized"
                >
                  <VehicleDetailPage />
                </RequireRole>
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole
                  allowedRoles={['admin']}
                  redirectTo="/unauthorized"
                >
                  <VehicleEditPage />
                </RequireRole>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'rfid/transfer-matching-configs',
        children: [
          {
            path: '',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole
                  allowedRoles={['admin']}
                  redirectTo="/unauthorized"
                >
                  <TransferMatchingConfigListPage />
                </RequireRole>
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole
                  allowedRoles={['admin']}
                  redirectTo="/unauthorized"
                >
                  <TransferMatchingConfigCreatePage />
                </RequireRole>
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole
                  allowedRoles={['admin']}
                  redirectTo="/unauthorized"
                >
                  <TransferMatchingConfigEditPage />
                </RequireRole>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'rfid/notifications',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <RequireRole
              allowedRoles={['admin']}
              redirectTo="/unauthorized"
            >
              <NotificationListPage />
            </RequireRole>
          </Suspense>
        ),
      },
      // Other routes omitted for brevity
    ],
  },
  // Workflow approval email link — decodes token and redirects, no auth required here
  {
    path: 'workflow-approval/:token',
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <WorkflowApprovalRedirect />
      </Suspense>
    ),
  },
  // Dedicated logout URL — outside RequireAuth so it works regardless of auth state
  {
    path: 'logout',
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <Logout />
      </Suspense>
    ),
  },
  // Auth routes — invite is Auth0 only; callback is Authentik only
  ...authRoutes,
  // Redirects for deprecated RFID Management routes
  {
    path: 'rfid-management',
    element: <Navigate to="/rfid/readers" />,
  },
  {
    path: 'rfid-management/add-reader/:type',
    element: <Navigate to="/rfid/readers/create" />,
  },
  {
    path: 'rfid-management/edit-reader/:id',
    element: <Navigate to="/rfid/readers/:id/edit" replace />,
  },
  {
    path: 'rfid-management/assign-location/:id',
    element: <Navigate to="/rfid/readers/:id/edit" replace />,
  },
  {
    path: 'rfid-management/add-reader-type',
    element: <Navigate to="/rfid/reader-types/create" />,
  },
  {
    path: 'rfid-management/edit-reader-type/:id',
    element: <Navigate to="/rfid/reader-types/:id/edit" replace />,
  },
  // Catch-all route for 404
  {
    path: '*',
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <NotFound />
      </Suspense>
    ),
  },
  // Unauthorized access route
  {
    path: 'unauthorized',
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <Unauthorized />
      </Suspense>
    ),
  },
];

export default routes;
